import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Chip, IconButton, Paper, Typography } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { ReactNode } from "react";

type Props = {
	title?: string;
	count?: number;
	subtitle?: ReactNode;
	actions?: ReactNode;
	children: ReactNode;
};

// Column classes for the grid inside a panel: the global theme centers every cell, these align
// text left, numbers right and the row menu to the edge
export const panelColumn = {
	text: { headerClass: "panel-left", cellClass: "panel-left" },
	number: { headerClass: "panel-right", cellClass: "panel-right" },
	menu: { headerClass: "panel-menu", cellClass: "panel-menu" },
};

// Last column of a panel grid: three dots that open the row's context menu
export const rowMenuColumn: ColDef = {
	colId: "actions",
	headerName: "",
	width: 56,
	sortable: false,
	resizable: false,
	...panelColumn.menu,
	cellRenderer: (p: ICellRendererParams) => (
		<IconButton
			size="small"
			aria-label="actions"
			onClick={(e) =>
				// biome-ignore lint/suspicious/noExplicitAny: mouseEvent is missing from the typings
				(p.api as any).showContextMenu({
					column: p.column,
					rowNode: p.node,
					mouseEvent: e.nativeEvent,
				})
			}
		>
			<MoreVertIcon fontSize="small" />
		</IconButton>
	),
};

// A list inside a Configuration page: its own header (name and count, or just a muted status
// line on single-list pages, on the left; the list's actions on the right) above the content,
// so each button sits in the box it acts on
export const ConfigurationPanel = ({
	title,
	count,
	subtitle,
	actions,
	children,
}: Props) => (
	<Paper
		elevation={1}
		sx={{
			borderRadius: "0.75rem",
			overflow: "hidden",
			display: "flex",
			flexDirection: "column",
			".ag-root-wrapper": { border: "none", borderRadius: 0 },
			".ag-header-cell.panel-left .ag-header-cell-label, .ag-header-cell.panel-menu .ag-header-cell-label":
				{
					justifyContent: "flex-start !important",
					textAlign: "left !important",
				},
			".ag-cell.panel-left": {
				justifyContent: "flex-start !important",
				textAlign: "left !important",
			},
			".ag-header-cell.panel-right .ag-header-cell-label": {
				justifyContent: "flex-end !important",
				textAlign: "right !important",
			},
			".ag-cell.panel-right": {
				justifyContent: "flex-end !important",
				textAlign: "right !important",
			},
			".ag-header-cell, .ag-cell": { padding: "0 16px !important" },
			".ag-header-cell[aria-colindex='1'], .ag-cell[aria-colindex='1']": {
				paddingLeft: "20px !important",
			},
			".ag-cell.panel-menu": {
				justifyContent: "flex-end !important",
				padding: "0 8px !important",
			},
		}}
	>
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 2,
				minHeight: title && subtitle ? 72 : 64,
				px: 2.5,
				py: 1.5,
				borderBottom: 1,
				borderColor: "divider",
				flexShrink: 0,
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				{title && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography
							sx={{ fontSize: 16, fontWeight: 600, lineHeight: "24px" }}
						>
							{title}
						</Typography>
						{count !== undefined && (
							<Chip
								label={count}
								size="small"
								sx={{ height: 20, fontSize: 12, fontWeight: 500 }}
							/>
						)}
					</Box>
				)}
				{subtitle && (
					<Typography
						variant="body2"
						sx={{ color: "text.secondary", mt: title ? 0.25 : 0 }}
					>
						{subtitle}
					</Typography>
				)}
			</Box>
			{actions && (
				<Box sx={{ ml: "auto", display: "flex", gap: 1 }}>{actions}</Box>
			)}
		</Box>
		{children}
	</Paper>
);
